//@flow
import * as React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { translate } from 'react-i18next';
import { withRouter } from 'common/components/core/router';
import _ from 'lodash';
import { serialize } from 'object-to-formdata';
import EnvConfig from 'config';

import { getFormData } from 'common/redux/selectors/zendeskSelector';
import { ZendeskWidgetFactory } from 'common/components/core/common/ZendeskWidget';
import { ApiModel, ApiError } from 'common/modules/api';
import StringUtils from 'common/utils/StringUtils';
import update from 'immutability-helper';
import { UploadStatusEnum } from 'common/components/core/dropzone/DragDrop';
import DialogStatusEnum from 'common/enums/DialogStatusEnum';
import { DashboardURLEnum } from 'common/enums/DashboardURLEnum';
import { getCustomLocationMatch } from 'common/utils/URLUtils';
import { TicketTypeEnum } from 'common/enums/ZendeskEnum';
import { getTeamNameAndColor } from 'common/redux/selectors/teamSelector';
import { TicketingProviderItems } from 'common/models/internal/Client';
import { Personnel } from 'common/models/internal/Personnel';
import Base64 from 'common/utils/Base64';
import { type ThemeType } from 'react-css-themr';

type OwnProps = {
    theme: ThemeType,
    personnel: Personnel,
    ticketType: $Values<TicketTypeEnum>,
    icon: string,
    dialogHeader: string,
    tooltipText: string,
    extraTicketItems?: Array<TicketingProviderItems>,
    ticketingURL?: string
};
type Props = {
    ...OwnProps,
    formData: $FixMe,
    isEditOrViewPlanScreen: boolean,
    personnelOrganization: string,
    teamName: ?string
};

export type FormDataType = {
    name: string,
    email: string,
    subject: string,
    message: string,
    organization?: string
};

type State = {
    status: string,
    error: string,
    dialogActive: boolean,
    formData: FormDataType,
    zendeskResponse: ZendeskResponseType
};

type ZendeskResponseType = {
    id: number
};

type Attachment = {
    id: number,
    name: string,
    content_url: string,
    content_type: string,
    size: number,
    thumbnails: ?Array<Attachment>
};

export type Comment = {
    id?: number,
    body: string,
    html_body?: string,
    author_id?: number,
    attachments?: Array<Attachment>,
    created_at?: string
};

type ZendeskRequestType = {
    request: {
        subject: string,
        comment: Comment,
        requester: { name: string, email: string, organization?: string }
    }
};
type HeadersType = {
    [key: string]: string
};

type ZendeskRequestParams = {
    body: ZendeskRequestType,
    headers: HeadersType
};

const fieldFormatType = {
    SINGLE_CHOICE: 'select',
    BOOLEAN: 'boolean',
    TEXT_FIELD: 'text'
};

const ZendeskWidgetContainerFactory = ({ ActionComponent } = {}) => {
    const ZendeskWidget = ZendeskWidgetFactory({ ActionComponent });
    class ZendeskWidgetContainer extends React.PureComponent<Props, State> {
        state: State;
        constructor(props: Props) {
            super(props);
            this.state = {
                status: DialogStatusEnum.Initial,
                uploadStatus: 'initial',
                dialogActive: false,
                error: '',
                formData: props.formData,
                isUploadValid: false,
                uploadError: null,
                isFormValid: false,
                formInputs: null,
                zendeskResponse: {
                    request: {}
                }
            };
        }
        static zendeskEndPoint = null;

        componentDidMount() {
            const { formData } = this.props;
            this.setState({
                formInputs: this.getInputsFromData(formData)
            });
        }

        UNSAFE_componentWillReceiveProps(nextProps) {
            const { formData } = this.props;
            const { formData: nextFormData } = nextProps;
            // this could become a costly operation right now it takes 0.05ms
            if (!_.isEqual(formData, nextFormData)) {
                this.setState({
                    formInputs: this.getInputsFromData(nextFormData)
                });
            }
        }

        buildCollaborators = ccEmails => {
            return ccEmails.value.split(',').map(ccEmail => {
                return { email: ccEmail.trim(), name: ccEmail.trim() };
            });
        };

        buildZendeskRequestParams = (
            formData: FormDataType,
            uploadsToken: ?string
        ): ZendeskRequestParams => {
            const { personnelOrganization } = this.props;
            const {
                name,
                email,
                ccEmails,
                phone,
                subject,
                message,
                url,
                team,
                ...rest
            } = formData;
            const currentUrl = window.location.href;
            const userUrl = url && url.value;
            const phoneValue = phone && phone.value;
            const teamValue = team && team.value;
            const comment = {
                body: `${message.value}
                            \n
                            ---
                            \n
                            Current URL: ${currentUrl}
                            ${userUrl ? `\nUser URL: ${userUrl}` : ''}
                            ${phoneValue ? `\nPhone: ${phoneValue}` : ''}
                            ${teamValue ? `\nUser Team: ${teamValue}` : ''}
                            `,
                uploads: uploadsToken ? [uploadsToken] : []
            };
            const collaborators = ccEmails && this.buildCollaborators(ccEmails);
            const customFields = Object.values(rest).map(fieldValue => ({
                id: fieldValue.fieldId,
                value: fieldValue.inputValue.value
            }));
            const body = {
                request: {
                    requester: {
                        name: name.value,
                        email: email.value,
                        organization: personnelOrganization
                    },
                    subject: subject.value,
                    comment,
                    collaborators,
                    custom_fields: customFields
                }
            };
            const headers = {
                'Content-Type': 'application/json'
            };
            return { body, headers };
        };

        buildFreshdeskRequestParam = (formData: FormDataType) => {
            const {
                name,
                email,
                ccEmails,
                phone,
                subject,
                message,
                url,
                team,
                attachments,
                ...rest
            } = formData;
            const currentUrl = window.location.href;
            const userUrl = url && url.value;
            const phoneValue = phone && phone.value;
            const teamValue = team && team.value;

            const ccEmailsList = ccEmails?.value
                ? ccEmails.value.split(',').length > 0
                    ? ccEmails.value.split(',')
                    : []
                : [];
            const customFields = Object.values(rest).reduce(
                (acc, fieldValue) => {
                    acc[`cf_${fieldValue.fieldId}`] =
                        fieldValue.inputValue.value;
                    return acc;
                },
                {}
            );

            let customAttachments = null;
            if (attachments.value) {
                const { files } = attachments.value;
                customAttachments = files.map(file => file.file);
            }

            let body = {
                name: name.value,
                email: email.value,
                priority: 1,
                status: 2,
                subject: subject.value,
                description: `
                    <div dir="ltr">
                        ${message.value}
                        <br>
                        ---
                        <br>
                        Current URL: ${currentUrl}
                        ${userUrl ? `\nUser URL: ${userUrl}` : ''}
                        ${phoneValue ? `\nPhone: ${phoneValue}` : ''}
                        ${teamValue ? `\nUser Team: ${teamValue}` : ''}
                    </div>
                `,
                cc_emails: ccEmailsList,
                custom_fields: customFields
            };

            if (customAttachments?.length) {
                body = {
                    ...body,
                    attachments: customAttachments
                };
            }

            const fd = serialize(body);

            return {
                body: customAttachments?.length ? fd : body
            };
        };

        sendFreshdeskRequest = (body: any) => {
            this.setState({ status: DialogStatusEnum.Loading });

            const freshdeskRequestsApi = new ApiModel({
                resourceName: 'freshdeskRequest',
                baseUrl: EnvConfig.freshdesk.baseUrl,
                endPoint: '/api/v2/tickets/',
                noQueryParams: true,
                isExternal: true
            });

            const customHeaders = {
                Authorization: `Basic ${Base64.encode(
                    `${EnvConfig.freshdesk.apiKey}:x`
                )}`
            };

            freshdeskRequestsApi
                .post({}, body, customHeaders)
                .then(response => {
                    this.setState({
                        zendeskResponse: response,
                        status: DialogStatusEnum.Success
                    });
                })
                .catch(error => {
                    if (error instanceof ApiError) {
                        this.setState({
                            error,
                            status: DialogStatusEnum.Error
                        });
                    } else {
                        this.setState({
                            error,
                            status: DialogStatusEnum.Error
                        });
                        throw error;
                    }
                });
        };

        sendZendeskRequest = (
            body: ZendeskRequestType,
            headers: HeadersType
        ) => {
            this.setState({ status: DialogStatusEnum.Loading });
            const zendeskRequestsApi = new ApiModel({
                resourceName: 'zendeskRequest',
                baseUrl: ZendeskWidgetContainer.zendeskEndPoint,
                endPoint: `/requests.json/`,
                noQueryParams: true,
                isExternal: true
            });
            zendeskRequestsApi
                .post({}, body, headers)
                .then(response => {
                    this.setState({
                        zendeskResponse: response,
                        status: DialogStatusEnum.Success
                    });
                })
                .catch(error => {
                    if (error instanceof ApiError) {
                        this.setState({
                            error,
                            status: DialogStatusEnum.Error
                        });
                    } else {
                        this.setState({
                            error,
                            status: DialogStatusEnum.Error
                        });
                        throw error;
                    }
                });
        };

        onDialogOpen = () => {
            const { formData } = this.props;
            const { uploadError, isUploadValid } = this.validateUploads(
                formData
            );
            const formInputs = this.getInputsFromData(formData);
            const isFormValid = this.validateForm(formInputs) && isUploadValid;
            this.setState({
                dialogActive: true,
                formInputs,
                formData,
                isFormValid,
                isUploadValid,
                uploadError
            });
        };

        onDialogClose = () => {
            this.setState({
                dialogActive: false,
                status: DialogStatusEnum.Initial,
                error: ''
            });
        };

        onHelpClick = () => {
            const { formData } = this.state;
            const { ticketType, ticketingURL } = this.props;
            this.setState({ status: DialogStatusEnum.Loading });
            if (ticketType === TicketTypeEnum.EditPlanApproval) {
                ZendeskWidgetContainer.zendeskEndPoint = ticketingURL;
                const { body, headers } = this.buildZendeskRequestParams(
                    formData
                );
                this.sendZendeskRequest(body, headers);
            } else {
                const { body } = this.buildFreshdeskRequestParam(formData);
                this.sendFreshdeskRequest(body);
            }
        };

        onFormChange = formData => {
            const { uploadError, isUploadValid } = this.validateUploads(
                formData
            );
            const isFormValid = this.validateForm(formData) && isUploadValid;
            const formInputs = this.getInputsFromData(formData);
            this.setState({
                formData,
                isFormValid,
                isUploadValid,
                uploadError,
                formInputs
            });
        };

        uploadFiles = formData => {
            const { files } = formData.attachments.value;
            // Create a new empty promise
            var sequence = Promise.resolve();
            // Loop over each file, and add on a promise to the
            // end of the 'sequence' promise.
            files.forEach((file, fileIndex) => {
                // Chain one computation onto the sequence
                sequence = sequence.then(uploadToken => {
                    return this.uploadFile(
                        file.file,
                        fileIndex,
                        formData,
                        uploadToken
                    );
                });
            });

            // This will resolve after the entire chain is resolved
            return sequence;
        };

        updateFormDataAttachments = (fileIndex, updateKey, updateData) => {
            this.setState(currentState => {
                const newFormData = update(currentState.formData, {
                    attachments: {
                        value: {
                            files: {
                                [fileIndex]: {
                                    [updateKey]: { $set: updateData }
                                }
                            }
                        }
                    }
                });
                return { formData: newFormData };
            });
        };

        removeFileFromAttachments = (formData, fileIndex: number) => {
            return update(formData, {
                attachments: {
                    value: {
                        files: { $splice: [[fileIndex, 1]] }
                    }
                }
            });
        };

        onRemoveFile = (fileIndex: number) => {
            const { formData } = this.state;
            const newFormData = this.removeFileFromAttachments(
                formData,
                fileIndex
            );
            this.onFormChange(newFormData);
        };

        uploadFile = (file, fileIndex, formData, uploadToken = ' ') => {
            this.updateFormDataAttachments(
                fileIndex,
                'fileStatus',
                UploadStatusEnum.Uploading
            );
            return new Promise((resolve, reject) => {
                var oReq = new XMLHttpRequest();
                oReq.open(
                    'POST',
                    // eslint-disable-next-line max-len
                    `${ZendeskWidgetContainer.zendeskEndPoint}/uploads.json/?filename=${file.name}&token=${uploadToken}`,
                    true
                );
                oReq.responseType = 'json';
                oReq.onreadystatechange = () => {
                    if (oReq.readyState === XMLHttpRequest.DONE) {
                        if (oReq.status === 201) {
                            this.updateFormDataAttachments(
                                fileIndex,
                                'fileStatus',
                                UploadStatusEnum.Success
                            );
                            resolve(oReq.response.upload.token);
                        } else {
                            if (oReq.status > 201) {
                                this.updateFormDataAttachments(
                                    fileIndex,
                                    'fileStatus',
                                    UploadStatusEnum.Fail
                                );
                                this.updateFormDataAttachments(
                                    fileIndex,
                                    'fileError',
                                    oReq.response
                                );

                                this.setState({
                                    status: DialogStatusEnum.Error
                                });
                                reject(oReq.response);
                            } else {
                                reject(oReq.response);
                            }
                        }
                    }
                };
                var blob = file;

                oReq.send(blob);
            });
        };

        getInputsFromData = formData => {
            const { t, extraTicketItems } = this.props;
            const { status } = this.state;
            const allFields = {
                name: {
                    fieldId: 'name',
                    placeholder: t('enterYourName'),
                    inputValue: null,
                    type: 'text',
                    required: true
                },
                team: {
                    fieldId: 'team',
                    placeholder: t('enterYourTeam'),
                    inputValue: null,
                    type: 'text',
                    required: true
                },
                email: {
                    fieldId: 'email',
                    label: t('emailId'),
                    placeholder: t('enterYourEmail'),
                    inputValue: null,
                    type: 'email',
                    required: true
                },
                ccEmails: {
                    fieldId: 'ccEmails',
                    label: t('cc'),
                    labelMeta: t('optional'),
                    placeholder: t('enterCC'),
                    inputValue: null,
                    type: 'text',
                    pattern: '\\w+@\\w+\\.\\w+(,\\s*\\w+@\\w+\\.\\w+)*',
                    title: 'Comma seperated e-mails',
                    required: false
                },
                phone: {
                    fieldId: 'phone',
                    label: t('contactNumber'),
                    labelMeta: t('optional'),
                    placeholder: t('enterYourPhone'),
                    inputValue: null,
                    type: 'text',
                    required: false
                },
                subject: {
                    fieldId: 'subject',
                    label: t('subject'),
                    placeholder: t('enterSubject'),
                    inputValue: null,
                    type: 'text',
                    required: true
                },
                url: {
                    fieldId: 'url',
                    label: t('url'),
                    labelMeta: t('optional'),
                    placeholder: t('enterYourUrl'),
                    inputValue: null,
                    type: 'text',
                    required: false
                },
                message: {
                    fieldId: 'message',
                    label: t('message'),
                    placeholder: t('explainIssue'),
                    inputValue: null,
                    type: 'textarea',
                    required: true,
                    textareaRows: 4
                },
                attachments: {
                    fieldId: 'attachments',
                    label: t('attachments'),
                    labelMeta: 'If any',
                    placeholder: t('clickToUpload'),
                    inputValue: null,
                    onRemoveFile: this.onRemoveFile,
                    dialogStatus: status,
                    type: 'file'
                }
            };

            extraTicketItems &&
                extraTicketItems.forEach(ticketItem => {
                    allFields[ticketItem.fieldId] = {
                        fieldId: ticketItem.fieldId,
                        label: ticketItem.item,
                        placeholder: `Enter ${ticketItem.item}`,
                        inputValue: null,
                        type: fieldFormatType[ticketItem.format],
                        required: !ticketItem.optional
                    };

                    if (ticketItem.format === 'SINGLE_CHOICE') {
                        allFields[
                            ticketItem.fieldId
                        ].options = ticketItem.possibleValues
                            .map(possibleValue => ({
                                label: possibleValue,
                                value: possibleValue
                            }))
                            .toJS();
                    }
                });
            const inputList = _.map(formData, formItem => {
                const value =
                    formItem.value ||
                    (formItem.inputValue && formItem.inputValue.value) ||
                    '';
                return _.extend({}, allFields[formItem.fieldId], formItem, {
                    inputValue: { value }
                });
            });
            return inputList;
        };

        validateForm = (formData: FormDataType): boolean => {
            let isValid = true;
            _.forEach(formData, formItem => {
                switch (formItem.fieldId) {
                    // the email filed should only be validated when the email field is required
                    // otherwise any data is OK.
                    case 'email': {
                        if (
                            formItem.required &&
                            !StringUtils.isEmail(formItem.value) &&
                            isValid
                        ) {
                            isValid = false;
                        }
                        break;
                    }
                    case 'ccEmails': {
                        if (
                            formItem.required &&
                            StringUtils.isEmpty(formItem.value) &&
                            isValid
                        ) {
                            isValid = false;
                        } else {
                            if (!StringUtils.isEmpty(formItem.value)) {
                                _.split(formItem.value, ',').forEach(
                                    ccEmail => {
                                        if (
                                            !StringUtils.isEmail(ccEmail) &&
                                            isValid
                                        ) {
                                            isValid = false;
                                        }
                                    }
                                );
                            }
                        }
                        break;
                    }
                    default: {
                        if (
                            formItem.required &&
                            StringUtils.isEmpty(formItem.value) &&
                            isValid
                        ) {
                            isValid = false;
                        }
                        break;
                    }
                }
            });
            return isValid;
        };

        validateUploads = ({ attachments }) => {
            if (!attachments) {
                return { isUploadValid: true };
            } else {
                const totalFileSize = _.sumBy(
                    attachments.value.files,
                    file => file.file.size
                );
                if (totalFileSize > 1024 * 1024) {
                    const uploadFileSizeError = true;
                    return {
                        uploadError: uploadFileSizeError,
                        isUploadValid: false
                    };
                }
            }
            return { isUploadValid: true };
        };

        render() {
            const {
                status,
                error,
                dialogActive,
                uploadError,
                isFormValid,
                zendeskResponse,
                formInputs
            } = this.state;
            const {
                tooltipText,
                icon,
                dialogHeader,
                teamName,
                ...others
            } = this.props;
            return (
                <ZendeskWidget
                    {...others}
                    status={status}
                    error={error}
                    uploadError={uploadError}
                    issueId={zendeskResponse?.id}
                    onHelpClick={this.onHelpClick}
                    dialogActive={dialogActive}
                    onDialogClose={this.onDialogClose}
                    onDialogOpen={this.onDialogOpen}
                    onFormChange={this.onFormChange}
                    isFormValid={isFormValid}
                    formInputs={formInputs}
                    tooltipText={tooltipText}
                    icon={icon}
                    dialogHeader={dialogHeader}
                    teamName={teamName}
                />
            );
        }
    }

    const mapStateToProps = (state, ownProps) => {
        const personnelOrganization = ownProps.personnel?.get('clientId', '');
        const customLocationMatch = getCustomLocationMatch(ownProps.location);
        let isEditOrViewPlanScreen = false;
        if (customLocationMatch) {
            isEditOrViewPlanScreen =
                DashboardURLEnum.EditPlan ===
                    customLocationMatch.url.DashboardURLEnum ||
                DashboardURLEnum.ViewPlan ===
                    customLocationMatch.url.DashboardURLEnum;
        }
        const externalMatch = customLocationMatch && customLocationMatch.match;
        const { team: teamName } = getTeamNameAndColor(
            state,
            ownProps,
            externalMatch
        );
        const formData = getFormData(state, ownProps, {
            isEditOrViewPlanScreen,
            teamName
        });

        return {
            isEditOrViewPlanScreen,
            customLocationMatch,
            formData,
            personnelOrganization,
            teamName
        };
    };

    const withWrappers = compose(
        withRouter,
        connect(mapStateToProps),
        translate('zendesk')
    );

    return (withWrappers(
        ZendeskWidgetContainer
    ): React.AbstractComponent<OwnProps>);
};

export default ZendeskWidgetContainerFactory({});
export { ZendeskWidgetContainerFactory };
